import React, { useEffect, useRef, useCallback } from "react";
import { Container } from "components";
import { useLocation, useNavigate } from "react-router-dom";
import Breadcrumb from "components/molecules/Breadcrumb";
import { CKEditor } from "ckeditor4-react";
import { useDispatch, useSelector } from "react-redux";
import { getData, postData, putData } from "actions";
import * as Yup from "yup";
import { API_URL_templateType, API_URL_templates } from "constants/api";
import { templateTypeReducers } from "redux/templateTypeSlice";
import { useFormik } from "formik";
import { templatesReducers } from "redux/templatesSlice";

const FormTemplates = ({ typeForm }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const { getTemplateTypeResult } = useSelector((state) => ({
    getTemplateTypeResult: state.templateType.getTemplateTypeResult,
  }));

  const getTemplateType = useCallback(
    async (params) => {
      getData(
        API_URL_templateType,
        params,
        { dispatch, redux: templateTypeReducers },
        "GET_TEMPLATETYPE"
      );
    },
    [dispatch]
  );

  const formik = useFormik({
    initialValues: {
      name: location.state ? location.state.item.name : "",
      use_subject: location.state ? location.state.item.use_subject : 0,
      type: location.state ? location.state.item.type : "",
      subject: location.state ? location.state.item.subject : "",
      message: location.state ? location.state.item.message : "",
      set: location.state ? location.state.item.set : "",
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Type is required"),
      type: Yup.string().required("Type is required"),
    }),
    onSubmit: (values) => {
      const formData = values;
      const item = { id: values.set };
      if (typeForm === "create") {
        postData(
          API_URL_templates,
          formData,
          { dispatch, redux: templatesReducers },
          "ADD_TEMPLATES",
          () => {
            navigate("/administrator/email/templates/view", {
              state: {
                item,
              },
            });
          }
        );
      } else {
        putData(
          `${API_URL_templates}${location.state.item.id}/`,
          formData,
          { dispatch, redux: templatesReducers },
          "ADD_TEMPLATES",
          () => {
            navigate("/administrator/email/templates/view", {
              state: {
                item,
              },
            });
          }
        );
      }
    },
  });

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    const params = ``;
    getTemplateType(params);
  }, [getTemplateType]);

  return (
    <Container>
      <Breadcrumb
        title={"Templates"}
        excludedPaths={["email", "administrator", "view"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          {typeForm === "create" ? "Create" : "Edit"} template
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-4">
              <div className="col-span-full mb-1.5">
                <label className="text-base mr-1 ">
                  Name <span className="text-red-500">*</span>
                </label>
                <div>
                  <input
                    id="name"
                    type="text"
                    name="name"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.name}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  {formik.touched.name && formik.errors.name && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.name}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5">
                <label className="text-xs mr-1 md:-text-sm lg:text-sm">
                  Template types <span className="text-red-500">*</span>
                </label>
                <div>
                  <select
                    id="type"
                    name="type"
                    value={formik.values.type}
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[1] = ref)}
                    className="block w-full md:w-2/3 lg:w-2/3 rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm focus:ring-1 focus:ring-inset focus:ring-blue-400 sm:text-sm sm:leading-6"
                  >
                    <option value="">Select</option>

                    {getTemplateTypeResult &&
                      getTemplateTypeResult.map((item, index) => (
                        <option key={index} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                  </select>
                  {formik.touched.type && formik.errors.type && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.type}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5">
                <label className="text-xs md:-text-sm lg:text-sm">Subjek</label>
                <div className="flex items-center flex-wrap">
                  <input
                    type="text"
                    name="subject"
                    id="subject"
                    value={formik.values.subject}
                    onChange={formik.handleChange}
                    className="block w-full mb-2 md:mb-0 lg:mb-0 md:w-2/3 lg:w-2/3 rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 text-xs md:-text-sm lg:text-sm sm:leading-6"
                  />
                  <label className="text-xs md:-text-sm lg:text-sm">
                    <input
                      id="use_subject"
                      type="checkbox"
                      name="use_subject"
                      checked={formik.values.use_subject === 1}
                      onChange={() =>
                        formik.setFieldValue(
                          "use_subject",
                          formik.values.use_subject === 1 ? 0 : 1
                        )
                      }
                      className="form-checkbox ml-0 md:ml-3 lg:ml-3 mr-2"
                    />
                    Use Subject
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 mb-5">
              <label className="text-xs mr-1 md:-text-sm lg:text-sm mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <div>
                <CKEditor
                  config={toolbarConfig}
                  type="classic"
                  initData={formik.values.message}
                  onChange={(e) =>
                    formik.setFieldValue("message", e.editor.getData())
                  }
                />
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <div className="flex justify-between">
              <button
                type="button"
                onClick={(e) => window.history.back()}
                className="flex  items-center gap p-1 px-3 rounded shadow-sm border text-white bg-yellow-800 hover:bg-yellow-700"
              >
                Back
              </button>
              <button
                type="submit"
                className="flex  items-center gap p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
              >
                Submit
              </button>
            </div>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default FormTemplates;
